import React, { useRef, useState } from 'react';
import { ProviderClaimDetails, ProviderMessage } from '@enter/core';
import { ProviderMessaging } from './messaging';
import { MessagingPresenter } from './messaging.presenter';

export interface IMessagingContextProps {
    claimId?: string;
    guarantorId?: string;
    isComposeOpen?: boolean;
    messageId?: string;
    message?: ProviderMessage;
    claimDetails?: ProviderClaimDetails;
    defaultSubject?: string;
    defaultBody?: string;
    onAssignDocument?: () => void;
}

export interface IProviderMessagingContextProps {
    openPanel: ((props: IMessagingContextProps) => void) | undefined;
    closePanel: (() => void) | undefined;
}

export const ProviderMessagingContext = React.createContext<IProviderMessagingContextProps>({
    openPanel: undefined,
    closePanel: undefined,
});

export const ProviderMessagingProvider: React.FC<{ children?: React.ReactNode }> = ({ children }): React.ReactElement => {
    const [contextProps, setContextProps] = useState<IMessagingContextProps>();
    const { current: presenter } = useRef(new MessagingPresenter());

    const openPanel = (props: IMessagingContextProps): void => {
        presenter.setOpen(true);
        
        if (props.message) {
            // Use message object directly (for messaging page - faster)
            presenter.openMessage(props.message, props.claimDetails);
        } else if (props.messageId) {
            // Use messageId only - fetch fresh data from API (for claim details)
            presenter.openMessageById(props.messageId);
        }

        if (props.isComposeOpen) {
            presenter.setComposeOpen(props.isComposeOpen);
        }

        // Store derived messageId in contextProps for consistency
        setContextProps({
            ...props,
            messageId: props.message?.id || props.messageId,
        });
    };

    const closePanel = (): void => {
        presenter.setOpen(false);
        setContextProps(undefined);
    };

    return (
        <ProviderMessagingContext.Provider value={{ openPanel, closePanel }}>
            {children}
            {contextProps && (
                <ProviderMessaging
                    presenter={presenter}
                    claimId={contextProps.claimId}
                    guarantorId={contextProps.guarantorId}
                    messageId={contextProps.messageId}
                    defaultBody={contextProps.defaultBody}
                    defaultSubject={contextProps.defaultSubject}
                    onAssignDocument={contextProps.onAssignDocument}
                    onClose={closePanel}
                />
            )}
        </ProviderMessagingContext.Provider>
    );
};
